import { ActivatedRoute } from '@angular/router';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { Observable } from 'rxjs';

import { EntityMemberCommunicationType } from '@state/entities';
import { EntityService, MessageTemplate } from '@state/entity';
import { FroalaHelper } from '@app/shared/services/vendor';
import { inputEmailPattern } from '@helpers/validation-patterns';
import { LaunchDarklyRepository } from '@state/launch-darkly';

export interface MessageInputData {
  bccRecipients?: string;
  ccRecipients?: string;
  communicationTypes?: EntityMemberCommunicationType[];
  fromAlias?: string;
  message?: string;
  notifyTeam?: boolean;
  replyTo?: string;
  subject?: string;
  unverifiedPrimaryContacts?: boolean;
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-message-input',
  styleUrls: ['./message-input.component.scss'],
  templateUrl: './message-input.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class MessageInputComponent implements OnInit {
  enableCommunicationPreferences$ = this.launchDarklyRepository.enableCommunicationPreferences$;

  @Input() bccRecipients: string;
  @Input() ccRecipients: string;
  @Input() communicationTypes: EntityMemberCommunicationType[] = [];
  @Input() disableAdvancedOptions: boolean;
  @Input() disableReplyTo: boolean;
  @Input() email: string;
  @Input() enableCommunicationPreferences?: boolean;
  @Input() enableFromAlias: boolean;
  @Input() excludeCcBccFields: boolean;
  @Input() froalaOptions: any;
  @Input() fromAlias: string;
  @Input() fullName: string;
  @Input() message: string;
  @Input() messageRequired = true;
  @Input() noTemplate: boolean;
  @Input() recipientLabel: string;
  @Input() replyTo: string;
  @Input() showNameAndEmail: boolean;
  @Input() subject: string;

  @Output() bccRecipientsChange = new EventEmitter();
  @Output() ccRecipientsChange = new EventEmitter();
  @Output() communicationTypesChange = new EventEmitter();
  @Output() emailChange = new EventEmitter();
  @Output() fromAliasChange = new EventEmitter();
  @Output() fullNameChange = new EventEmitter();
  @Output() messageChange = new EventEmitter();
  @Output() replyToChange = new EventEmitter();
  @Output() subjectChange = new EventEmitter();

  froalaMessageValue: string;
  messageTemplates$: Observable<MessageTemplate[]>;
  selectedMessageTemplate: MessageTemplate;
  showAdvancedOptions = false;
  inputEmailPattern = inputEmailPattern;

  constructor(
    public froalaHelper: FroalaHelper,
    private entityService: EntityService,
    private launchDarklyRepository: LaunchDarklyRepository,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.froalaMessageValue = this.message;
    this.messageTemplates$ = this.entityService.getMessageTemplates$(this.orgEntityId);
  }

  get froalaMessage(): string {
    return this.froalaMessageValue;
  }

  set froalaMessage(value: string) {
    this.messageChange.emit(value);
    this.froalaMessageValue = value;
  }

  get orgEntityId(): string {
    return this.route.root.firstChild.snapshot.params['membership_entity_id'] as string;
  }

  emailChangeEvent(email: string): void {
    this.emailChange.emit(email.trim());
  }

  fromAliasChangeEvent(alias: string): void {
    this.fromAliasChange.emit(alias.trim());
  }

  fullNameChangeEvent(name: string): void {
    this.fullNameChange.emit(name.trim());
  }

  communicationTypesChangeEvent(communictionTypes: EntityMemberCommunicationType[]): void {
    this.communicationTypesChange.emit(communictionTypes);
  }

  selectTemplate(): void {
    this.subject = this.selectedMessageTemplate.subject;
    this.froalaMessageValue = this.selectedMessageTemplate.body;
    this.subjectChange.emit(this.subject);
    this.messageChange.emit(this.froalaMessageValue);
  }

  toggleAdvancedOptions(): void {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }
}
